// angular dependencies
import { inject, Injectable } from '@angular/core';

// third-party dependencies
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// fantaz dependencies
import { HttpParams } from '@angular/common/http';
import type {
  CasualTournamentDetails,
  FetchNextEventsInput,
  FetchPendingEventsInput,
  FetchTournamentDetailsInput,
  FetchTournamentRoomsNextEventsInput,
  NextEvent,
  PendingEvent,
  PrototypeId,
  ScheduleId,
} from '@fantaz/shared/data-access/models';
import { EventSchedule } from '@fantaz/shared/data-access/models';
import { BlockDeviceService } from '@fantaz/shared/utils/block-device';
import { FantazHttpClient } from '../../../utils/api-http.service';
import { AuthServiceInjectionToken } from '../../auth';
import { UtilsService } from '../../utils.service';
import { TournamentsService } from '../tournaments-service.interface';

type FetchNextEventsResponse = {
  eventInstances: NextEvent[];
};

type FetchPendingEventsResponse = {
  eventInstances: Omit<PendingEvent, 'user'>[];
};

@Injectable()
export class TournamentsHttpApiService implements TournamentsService {
  #authService = inject(AuthServiceInjectionToken);
  #httpClient = inject(FantazHttpClient);

  utilsService = inject(UtilsService);
  blockDevice = inject(BlockDeviceService);

  private compare(a: EventSchedule, b: EventSchedule) {
    const prizeA = a.firstPrize?.prizeValue
      ? Number(a.firstPrize?.prizeValue + '')
      : 0;
    const prizeB = b.firstPrize?.prizeValue
      ? Number(b.firstPrize?.prizeValue + '')
      : 0;
    if (
      prizeA !== null &&
      prizeA !== undefined &&
      prizeB !== null &&
      prizeB !== undefined
    ) {
      return (
        a.entryAmount - b.entryAmount ||
        a.totalEntryCount - b.totalEntryCount ||
        prizeA - prizeB
      );
    }
    return 0;
  }

  fetchNextEvents(options: FetchNextEventsInput): Observable<NextEvent[]> {
    return this.#authService.isLoggedIn().pipe(
      switchMap((isLoggedIn) => {
        return this.#httpClient
          .get<FetchNextEventsResponse>(
            `api/${isLoggedIn ? 'user/' : ''}tournament/${options.scheduleId}/${
              options.prototypeId
            }/next-events`,
          )
          .pipe(map((response) => response.eventInstances.slice(0, 2)));
      }),
    );
  }

  fetchPendingEvents(
    options: FetchPendingEventsInput,
  ): Observable<PendingEvent[]> {
    return this.#httpClient
      .get<FetchPendingEventsResponse>(
        `api/core/user/tournament/${options.scheduleId}/${options.prototypeId}/pending-events`,
        {
          params: new HttpParams()
            .set('pageIndex', options.pageIndex?.toString() ?? '0')
            .set('pageSize', options.pageSize?.toString() ?? '10'),
        },
      )
      .pipe(map((response) => response.eventInstances || []));
  }

  fetchTournamentDetails(
    options: FetchTournamentDetailsInput,
  ): Observable<CasualTournamentDetails | null> {
    return this.#httpClient
      .get<CasualTournamentDetails>(
        `api/public/tournaments/${options.tournamentUrl}`,
      )
      .pipe(
        map((response) => {
          return response || null;
        }),
      );
  }

  fetchTournamentRoomDetails(
    options: FetchTournamentDetailsInput,
  ): Observable<CasualTournamentDetails> {
    return this.#httpClient.get<CasualTournamentDetails>(
      `api/public/tournaments/${options.tournamentUrl}`,
    );
  }

  fetchTournamentRoomsNextEvents(
    options: FetchTournamentRoomsNextEventsInput,
  ): Observable<Record<PrototypeId, Record<ScheduleId, NextEvent[]>>> {
    return this.#authService.isLoggedIn().pipe(
      switchMap((isLoggedIn) => {
        return this.#httpClient
          .post<Record<PrototypeId, Record<ScheduleId, NextEvent[]>>>(
            `api/${isLoggedIn ? 'user/' : ''}tournament/next-events`,
            {
              schedules: options.schedules,
            },
          )
          .pipe(
            map((response) => {
              return Object.keys(response).reduce<
                Record<PrototypeId, Record<ScheduleId, NextEvent[]>>
              >((acc, prototypeId) => {
                const schedulers = Object.keys(response[prototypeId]).reduce<
                  Record<ScheduleId, NextEvent[]>
                >((acc2, scheduleId) => {
                  return {
                    ...acc2,
                    [scheduleId]:
                      response[prototypeId][scheduleId].map(normalizeNextEvent),
                  };
                }, {});

                return {
                  ...acc,
                  [prototypeId]: {
                    ...acc[prototypeId],
                    ...schedulers,
                  },
                };
              }, {});
            }),
          );
      }),
    );
  }
}

function normalizeNextEvent(event: NextEvent): NextEvent {
  return {
    ...event,
    availableSeats: event.seats - event.participants?.length,
  };
}
